import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useContratos } from '@/hooks/useContratos'
import { useFaturas } from '@/hooks/useFaturas'
import { formatCurrency, getStatusBadgeClass } from '@/lib/formatters'
import { calculateContratoStatus, translateCalculatedStatus } from '@/lib/status-logic'
import { Car, ChevronDown, ChevronUp, Package, ShieldCheck, User } from 'lucide-react'
import { usePortalStore } from '@/stores/portal.store'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/config/supabase'

function useAllAsaasCharges(cpf: string | null | undefined) {
    return useQuery({
        queryKey: ['asaas-charges-bulk', cpf],
        queryFn: async () => {
            if (!cpf) return []
            const { data, error } = await supabase.functions.invoke('get-contract-charges', {
                body: { cpf, all_contracts: true }
            })
            if (error) throw error
            return data?.data || []
        },
        enabled: !!cpf,
        staleTime: 60_000,
    })
}

export function PortalContratosPage() {
    const navigate = useNavigate()
    const associado = usePortalStore(s => s.associado)
    const PORTAL_ASSOCIADO_ID = associado?.id || ''

    const rawCpf = usePortalStore(s => s.cpf)
    const portalCpf = rawCpf || associado?.cpf || ''
    
    const { data: result, isLoading: loadingContratos } = useContratos({ associado_id: PORTAL_ASSOCIADO_ID, cpf: portalCpf, pageSize: 50 })
    const { data: faturasResult, isLoading: loadingFaturas } = useFaturas({ associado_id: PORTAL_ASSOCIADO_ID, cpf: portalCpf, pageSize: 50 })
    const { data: asaasCharges, isLoading: loadingAsaas } = useAllAsaasCharges(portalCpf)

    const contratos = result?.data || []
    const faturas = faturasResult?.data || []

    const isLoading = loadingContratos || loadingFaturas || loadingAsaas

    return (
        <div className="p-4 space-y-4 animate-fade-in max-w-lg mx-auto">
            <div className="pt-2">
                <h1 className="text-xl font-bold text-surface-900">Meus Contratos</h1>
                <p className="text-sm text-surface-700/60">{contratos.length} contrato{contratos.length !== 1 ? 's' : ''}</p>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : contratos.length === 0 ? (
                <div className="text-center py-12">
                    <ShieldCheck size={40} className="text-surface-700/20 mx-auto mb-3" />
                    <p className="text-surface-700/50">Nenhum contrato encontrado</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {contratos.map(c => (
                        <ContratoCard 
                            key={c.id} 
                            contrato={c} 
                            faturas={faturas} 
                            asaasCharges={asaasCharges || []}
                            onNavigate={() => navigate(`/portal/contratos/${c.id}`)} 
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function ContratoCard({ contrato, faturas, asaasCharges, onNavigate }: { contrato: any; faturas: any[]; asaasCharges: any[]; onNavigate: () => void }) {
    const [expanded, setExpanded] = useState(false)
    const c = contrato
    
    // Status calculado a partir do Asaas (prioritário) ou locais
    let realStatus = c.status as any;
    if (realStatus !== 'cancelado' && realStatus !== 'suspenso') {
        const chargesForThis = asaasCharges.filter((ch: any) => {
            const ref = ch.external_reference || ''
            return ref === c.external_reference || 
                   ref === c.id || 
                   ref.startsWith(`${c.id}:`) || 
                   ref.startsWith(`${c.external_reference}:`)
        })

        if (chargesForThis.length > 0) {
            const isOverdue = chargesForThis.some((ch: any) => ch.status === 'OVERDUE')
            const isAdesaoPendente = chargesForThis.some((ch: any) => ch.status === 'PENDING' && (ch.description || '').toLowerCase().includes('ades'))
            
            if (isOverdue) realStatus = 'inadimplente'
            else if (isAdesaoPendente) realStatus = 'pendente_adesao'
            else realStatus = 'ativo'
        } else {
            realStatus = calculateContratoStatus(c, faturas)
        }
    }

    // Recria a lista de serviços a partir do metadata real originado do checkout
    const metadados: any = c.metadata || {}
    const servicosInclusos = []

    if (metadados.servico_nome) {
        servicosInclusos.push({
            id: 'svc-1',
            nome: metadados.servico_nome,
            valor_mensal: c.valor_total_mensal
        })
    } else {
        servicosInclusos.push({
            id: 'svc-2',
            nome: 'Proteção Básica',
            valor_mensal: c.valor_total_mensal
        })
    }

    return (
        <div className="rounded-2xl bg-white border border-surface-100 shadow-sm overflow-hidden">
            <button onClick={() => setExpanded(!expanded)}
                className="w-full p-4 flex items-start gap-3 text-left">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                    ${realStatus === 'ativo' ? 'bg-success-100' : realStatus === 'inadimplente' || realStatus === 'pendente_adesao' ? 'bg-warning-100' : 'bg-surface-100'}`}>
                    {c.veiculo_placa ? (
                        <Car size={20} className={
                            realStatus === 'ativo' ? 'text-success-600' : realStatus === 'inadimplente' || realStatus === 'pendente_adesao' ? 'text-warning-600' : 'text-surface-400'
                        } />
                    ) : (
                        <User size={20} className="text-blue-600" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                        <p className="font-mono font-bold text-surface-900">
                            {c.veiculo_placa || (c.metadata as any)?.placa || (c.metadata as any)?.servico_nome || 'Somente CPF'}
                        </p>
                        <span className={`badge ${getStatusBadgeClass(realStatus)}`}>{translateCalculatedStatus(realStatus)}</span>
                    </div>
                    <p className="text-xs text-surface-700/60 mt-0.5">
                        {c.veiculo_modelo || (c.metadata as any)?.placa ? 'Veículo Protegido' : 'Proteção pessoal'}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                        <p className="text-lg font-bold text-surface-900">{formatCurrency(c.valor_total_mensal)}<span className="text-xs font-normal text-surface-700/50">/mês</span></p>
                        {expanded ? <ChevronUp size={16} className="text-surface-400" /> : <ChevronDown size={16} className="text-surface-400" />}
                    </div>
                </div>
            </button>

            {expanded && (
                <div className="border-t border-surface-100 p-4 bg-surface-50/50 space-y-2 animate-fade-in">
                    <p className="text-xs font-medium text-surface-700/60 uppercase">Serviços inclusos</p>
                    {servicosInclusos.map((s: any) => (
                        <div key={s.id} className="flex items-center gap-2 py-1.5">
                            <Package size={14} className="text-primary-600" />
                            <span className="text-sm text-surface-900 flex-1">{s.nome}</span>
                            <span className="text-xs text-surface-700/60">{formatCurrency(s.valor_mensal)}</span>
                        </div>
                    ))}
                    <div className="pt-2 border-t border-surface-100 flex items-center justify-between">
                        <span className="text-xs text-surface-700/60">Vencimento dia {c.dia_vencimento}</span>
                        <div className="flex gap-2">
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onNavigate()
                                }}
                                className="px-3 py-1.5 bg-primary-600 text-white text-xs font-bold rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                Ver Detalhes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
